import React from 'react';

import type { ViewportName } from '../../../configs/responsive-config';
import type { cssValue } from '../../../sb-helpers/attrs-helper';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { colors } from '../../../theme/axiom-theme';
import { shadows } from '../../../theme/axiom-theme';
import { ColorUtil } from '../../../utils/color-util';

export const horizontalPositions = [
  'left',
  'center',
  'right',
  'space-between',
  'space-around',
] as const;

export const verticalPositions = [
  'top',
  'middle',
  'bottom',
  'stretch',
] as const;

type HorizontalPosition = (typeof horizontalPositions)[number];
type VerticalPosition = (typeof verticalPositions)[number];

export type LayoutPositionType =
  | HorizontalPosition
  | VerticalPosition
  | `${HorizontalPosition} ${VerticalPosition}`
  | `${VerticalPosition} ${HorizontalPosition}`;

export type LayoutProps = {
  background?: keyof typeof colors | CssBackgroundImage;
  border?: cssValue; // TODO APCORE-1551 Make this work: `${string}px ${string} ${keyof typeof colors}`
  borderLeft?: cssValue;
  borderRight?: cssValue;
  borderTop?: cssValue;
  borderBottom?: cssValue;
  borderRadius?: cssValue;
  boxShadow?: keyof typeof shadows;
  className?: string;
  children?: React.ReactNode;
  direction?: 'horizontal' | 'vertical';
  /**
   * A definite height, which is what a scroller inside needs: `stretched`'s `height: 100%` resolves
   * to nothing against a parent that has none, and `minHeight` leaves the box free to grow past it.
   */
  height?: cssValue;
  horizontalGutter?: cssValue;
  inline?: boolean;
  /**
   * A cap on the row or column itself, not on its items - `100%` width is the default, so this is
   * what stops a layout following a wide container. Pair it with `margin-inline: auto` on the
   * caller's side to centre what is left over.
   */
  maxWidth?: cssValue;
  /** A floor for the layout's height, which `stretched`'s `height: 100%` cannot give. */
  minHeight?: cssValue;
  name?: string;
  position?: LayoutPositionType;
  reversibleOn?: string | ViewportName;
  stackableOn?: string | ViewportName;
  verticalGutter?: cssValue;
  wrap?: boolean;
  /**
   * Fills the parent's height. **It does not stack the children** - the axis is `direction`'s alone,
   * which always has a value, so a `stretched` layout with no `direction` is a full-height row.
   */
  stretched?: boolean;
};

export const Layout = ({
  background,
  border,
  borderLeft,
  borderRight,
  borderTop,
  borderBottom,
  borderRadius,
  boxShadow,
  className,
  children,
  direction = 'horizontal',
  height,
  horizontalGutter,
  inline,
  maxWidth,
  minHeight,
  name,
  position,
  reversibleOn,
  stackableOn,
  verticalGutter,
  wrap,
  stretched,
}: LayoutProps) => {
  const positionTokens = position?.split(' ') ?? [];
  const positionHorizontal = positionTokens.find(value =>
    horizontalPositions.includes(value as HorizontalPosition)
  );
  const positionVertical = positionTokens.find(value =>
    verticalPositions.includes(value as VerticalPosition)
  );

  return (
    <div
      className={AttrsHelper.formatClassname(
        'layout-grid',
        `direction-${direction}`,
        inline && 'inline-flex',
        positionHorizontal && `horizontal-${positionHorizontal}`,
        positionVertical && `vertical-${positionVertical}`,
        reversibleOn
          ?.split(' ')
          .map(breakpoint => `reversible-${direction}-${breakpoint}`)
          .join(' '),
        stackableOn
          ?.split(' ')
          .map(breakpoint => `stackable-${breakpoint}`)
          .join(' '),

        wrap && 'wrap-content',
        stretched && 'layout-stretched',
        // The image form keeps the inline style below; there is no token for a url.
        typeof background === 'string' &&
          ColorUtil.tokenClass({ color: background, prefix: 'background' }),
        className
      )}
      data-test={name}
      style={{
        ...(background && typeof background === 'object'
          ? {
              backgroundImage: background.image && `url("${background.image}")`,
              backgroundAttachment: background.attachment,
              backgroundClip: background.clip,
              backgroundColor: background.color,
              backgroundPosition: background.position || 'right center',
              backgroundRepeat: background.repeat || 'no-repeat',
            }
          : {}),
        border: AttrsHelper.replaceColorNameWithHex(border),
        borderLeft: AttrsHelper.replaceColorNameWithHex(borderLeft),
        borderRight: AttrsHelper.replaceColorNameWithHex(borderRight),
        borderTop: AttrsHelper.replaceColorNameWithHex(borderTop),
        borderBottom: AttrsHelper.replaceColorNameWithHex(borderBottom),
        boxShadow: boxShadow && shadows[boxShadow],
        borderRadius: AttrsHelper.sanitizeCssValue(borderRadius),
        rowGap: AttrsHelper.sanitizeCssValue(verticalGutter),
        columnGap: AttrsHelper.sanitizeCssValue(horizontalGutter),
        height: AttrsHelper.sanitizeCssValue(height),
        maxWidth: AttrsHelper.sanitizeCssValue(maxWidth),
        minHeight: AttrsHelper.sanitizeCssValue(minHeight),
      }}
    >
      {children}
    </div>
  );
};
